import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { useChatStore } from '../store/chat-store';
import { EmojiPicker } from './EmojiPicker';
import { useVoiceRecorder } from '../lib/useVoiceRecorder';

interface Props {
  onSend: (text: string) => void;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  disabled?: boolean;
  /** When provided, a mic button is shown to record + send a voice note. */
  onSendVoice?: (blob: Blob, durationMs: number) => void;
}

const VOICE_MAX_SECONDS = 90;

const MAX = 2000;
const TYPING_PAUSE_MS = 2_000;

export function Composer({ onSend, onTypingStart, onTypingStop, disabled, onSendVoice }: Props) {
  const [text, setText] = useState('');
  const conn = useChatStore(s => s.connState);
  const cooldown = useChatStore(s => s.cooldown);
  const setCooldown = useChatStore(s => s.setCooldown);
  const ban = useChatStore(s => s.ban);
  const banMuted = ban?.type === 'mute';
  const taRef = useRef<HTMLTextAreaElement>(null);
  const isTypingRef = useRef(false);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // document.activeElement returns the shadow host (not the textarea) inside
  // a shadow DOM, so track focus explicitly for cursor-aware emoji insert.
  const focusedRef = useRef(false);

  const recorder = useVoiceRecorder({
    maxSeconds: VOICE_MAX_SECONDS,
    onDone: (blob, durationMs) => onSendVoice?.(blob, durationMs),
  });
  const recording = recorder.state === 'recording' || recorder.state === 'requesting';
  const voiceEnabled = !!onSendVoice && recorder.state !== 'unsupported';

  // Tick a remaining-seconds counter while a cooldown is active.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [cooldown]);

  const cooldownRemaining = cooldown ? Math.max(0, new Date(cooldown.untilAt).getTime() - now) : 0;
  const cooldownActive = !!cooldown && cooldownRemaining > 0;
  // Composer is locked when either a spam cooldown is active OR the user has
  // a persistent mute ban.
  const muted = cooldownActive || banMuted;

  // When a cooldown elapses, clear it from the store so the input unlocks.
  useEffect(() => {
    if (cooldown && cooldownRemaining === 0) setCooldown(null);
  }, [cooldown, cooldownRemaining, setCooldown]);

  const trimmed = text.trim();
  const canSend = !disabled && conn === 'connected' && trimmed.length > 0 && !muted;
  const overLimit = text.length >= MAX - 100;

  const fireTypingStop = () => {
    if (!isTypingRef.current) return;
    isTypingRef.current = false;
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    onTypingStop?.();
  };

  const noteUserActivity = (newText: string) => {
    if (!onTypingStart) return;
    if (newText.trim().length === 0) {
      // Field cleared: stop typing immediately if we were.
      fireTypingStop();
      return;
    }
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      onTypingStart();
    }
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(fireTypingStop, TYPING_PAUSE_MS);
  };

  // Cancel timers on unmount or disconnect.
  useEffect(() => {
    if (conn !== 'connected') {
      isTypingRef.current = false;
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    }
    return () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, [conn]);

  const submit = () => {
    if (!canSend) return;
    onSend(text);
    setText('');
    fireTypingStop();
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onChange = (val: string) => {
    const next = val.slice(0, MAX);
    setText(next);
    noteUserActivity(next);
    requestAnimationFrame(() => {
      if (!taRef.current) return;
      taRef.current.style.height = 'auto';
      taRef.current.style.height = `${Math.min(taRef.current.scrollHeight, 144)}px`;
    });
  };

  const insertEmoji = (emoji: string) => {
    const ta = taRef.current;
    // Insert at the caret if the textarea was focused; otherwise append.
    if (ta && focusedRef.current) {
      const start = ta.selectionStart ?? text.length;
      const end = ta.selectionEnd ?? text.length;
      const next = (text.slice(0, start) + emoji + text.slice(end)).slice(0, MAX);
      setText(next);
      noteUserActivity(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 144)}px`;
      });
    } else {
      const next = (text + emoji).slice(0, MAX);
      setText(next);
      noteUserActivity(next);
    }
  };

  const cooldownLabel = cooldownActive
    ? cooldownRemaining >= 60_000
      ? `Muted — ${Math.ceil(cooldownRemaining / 60_000)} min remaining`
      : `Slow down — wait ${Math.ceil(cooldownRemaining / 1000)}s`
    : null;

  const placeholder = banMuted
    ? `Muted by admin: ${ban?.reason ?? 'no reason given'}`
    : cooldownActive
      ? cooldownLabel ?? 'Cooldown…'
      : conn === 'connected' ? 'Type a message…' :
        conn === 'connecting' ? 'Connecting…' :
        conn === 'reconnecting' ? 'Reconnecting…' :
        'Disconnected';

  const recSecs = Math.floor(recorder.elapsedMs / 1000);
  const recLabel = `${Math.floor(recSecs / 60)}:${(recSecs % 60).toString().padStart(2, '0')}`;
  const showMic = voiceEnabled && !muted && conn === 'connected' && trimmed.length === 0;

  if (recording) {
    return (
      <div className="border-t border-gray-100 px-3 py-2.5 bg-white">
        <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50/60 px-3 py-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
          <span className="text-sm text-red-700 font-medium tabular-nums">
            {recorder.state === 'requesting' ? 'Allow microphone…' : `Recording ${recLabel}`}
          </span>
          <span className="text-[11px] text-red-400">/ {Math.floor(VOICE_MAX_SECONDS / 60)}:{(VOICE_MAX_SECONDS % 60).toString().padStart(2, '0')} max</span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={recorder.cancel}
            aria-label="Cancel recording"
            className="w-9 h-9 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <button
            type="button"
            onClick={recorder.stop}
            aria-label="Send voice message"
            className="vip-btn-primary w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-sm transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 px-3 py-2.5 bg-white">
      {recorder.state === 'denied' && (
        <div className="mb-1.5 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
          Microphone blocked — enable it in your browser, or just type a message.
        </div>
      )}
      <div className={`flex items-end gap-2 rounded-2xl border ${conn === 'connected' ? 'border-gray-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100' : 'border-gray-200'} bg-gray-50 transition px-2.5 py-1.5`}>
        <textarea
          ref={taRef}
          rows={1}
          value={text}
          onChange={e => onChange(e.target.value)}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={() => { focusedRef.current = false; fireTypingStop(); }}
          onKeyDown={onKey}
          disabled={disabled || conn !== 'connected' || muted}
          placeholder={placeholder}
          className={`flex-1 resize-none bg-transparent text-sm leading-5 placeholder:text-gray-400 focus:outline-none py-1.5 max-h-36 disabled:cursor-not-allowed ${muted ? 'placeholder:text-amber-700' : ''}`}
        />
        {overLimit && (
          <span className={`self-center text-[10px] tabular-nums ${text.length >= MAX ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
            {text.length}/{MAX}
          </span>
        )}
        {!muted && <div className="self-center"><EmojiPicker onPick={insertEmoji} /></div>}
        {showMic ? (
          <button
            type="button"
            onClick={recorder.start}
            aria-label="Record voice message"
            title="Record voice message"
            className="shrink-0 w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          </button>
        ) : (
          <button
            type="button"
            disabled={!canSend}
            onClick={submit}
            aria-label="Send message"
            className="vip-btn-primary shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-sm hover:from-blue-700 hover:to-indigo-700 transition disabled:from-gray-300 disabled:to-gray-300 disabled:bg-none disabled:cursor-not-allowed disabled:shadow-none"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        )}
      </div>
      <div className="mt-1 px-2 text-[10px] text-gray-400 select-none">
        Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 border border-gray-200">Enter</kbd> to send · <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 border border-gray-200">Shift</kbd> + <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 border border-gray-200">Enter</kbd> for new line
      </div>
    </div>
  );
}
